import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../../lib/utils';
import type { IconName } from '../blocks/Icon';
import { Icon } from '../blocks/Icon';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

export interface PageTab {
  value: string;
  label: string;
  /** Leading glyph. The design pairs every page-level tab with one. */
  icon?: IconName;
  disabled?: boolean;
}

type PageTabsProps = {
  tabs: PageTab[];
  children: ReactNode;
  className?: string;
} & (
  | { value: string; onValueChange: (value: string) => void; defaultValue?: never }
  | { defaultValue: string; value?: never; onValueChange?: never }
);

/**
 * Page-level section switcher: the `pills` tab bar that sits under a page heading and swaps between
 * sibling views. Stacks one tab per row on narrow containers and moves to a left-aligned row at 40rem.
 *
 * Tabs are content-width rather than stretched to fill, and the group stays left-aligned whichever
 * tab is active, so tabs never reposition under the pointer when one is selected.
 */
export function PageTabs({ tabs, children, className, value, defaultValue, onValueChange }: PageTabsProps) {
  const isURLMode = value !== undefined;

  // Base UI calls onValueChange with (value, eventDetails). Narrow it to the value so consumers keep
  // the plain (value: string) => void contract this component advertises.
  function handleValueChange(newValue: string) {
    onValueChange?.(newValue);
  }

  return (
    <Tabs
      variant="pills"
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleValueChange}
      className={cn('w-full', className)}
    >
      <TabsList>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            className="grow-0 basis-auto px-2 py-2 text-xs sm:px-4 sm:text-sm"
          >
            {tab.icon && <Icon name={tab.icon} className="text-[20px]" />}
            <span>{tab.label}</span>
          </TabsTrigger>
        ))}
      </TabsList>
      {isURLMode ? (
        <TabsContent value={value} className="mt-6">
          {children}
        </TabsContent>
      ) : (
        children
      )}
    </Tabs>
  );
}

export function PageTabPanel({ className, ...props }: ComponentProps<typeof TabsContent>) {
  return <TabsContent className={cn('mt-6', className)} {...props} />;
}
